import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { concatMap, shareReplay, Subject } from 'rxjs';
import {
  delay,
  filter,
  finalize,
  take,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs/operators';

import { AccountBackend } from '@libs/src/account/account.backend';
import { AnalyticsService } from '@libs/src/analytics/analytics.service';
import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { LoggerService } from '@libs/src/logger/logger.service';
import { PostHogService } from '@libs/src/posthog/post-hog.service';
import { ToastService } from '@libs/src/toast/toast.service';
import { ZendeskService } from '@libs/src/zendesk/zendesk.service';
import { AIAnnouncementModalService } from '@main-client/src/app/ai-announcement-modal/ai-announcement-modal.service';
import { AuthService } from '@main-client/src/app/auth/auth.service';
import { RouteListenerService } from '@main-client/src/app/core/route-listener.service';
import { UpdatesService } from '@main-client/src/app/core/updates.service';
import { TenantService } from '@main-client/src/app/tenant/tenant.service';
import { UserIdleTimeoutService } from '@main-client/src/app/user-idle-timeout/user-idle-timeout.service';
import { UserService } from '@main-client/src/app/user/user.service';

import { removeNonAngularErrorHandlers } from '@libs/src/logger/error-logger.helper';
import { Account } from '@libs/src/models/account.model';
import { POSTHOG_FEATURE_FLAGS } from '@libs/src/posthog/posthog-feature-flags.constants';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import { SetUser } from '@libs/src/user/user.actions';
import {
  FEATURES_SEEN,
  userHasSeenFeature,
} from '@main-client/src/app/account/account.helpers';
import { IAppState } from '@main-client/src/app/app.state';
import { isAnyAdmin } from '@main-client/src/app/user/user.helpers';

import { currentGroupSource } from '../group-view/group-view.helper';

import defer from 'lodash-es/defer';
import isEmpty from 'lodash-es/isEmpty';

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-root',
  standalone: false,
  templateUrl: './root.component.html',
})
export class RootComponent implements OnInit, OnDestroy {
  header = this.store.select('header');
  tenant = this.store.select('tenant');
  user = this.store.select('user');
  componentDestroy = new Subject<void>();
  isEmptyState = false;
  routeTitle: string;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly accountBackend: AccountBackend,
    private readonly aiAnnouncementModalService: AIAnnouncementModalService,
    private readonly analyticsService: AnalyticsService,
    private readonly authService: AuthService,
    private readonly destroyService: DestroySubscriptionsService,
    private readonly loggerService: LoggerService,
    private readonly postHogService: PostHogService,
    private readonly routeListenerService: RouteListenerService,
    private readonly router: Router,
    private readonly store: Store<IAppState>,
    private readonly tenantService: TenantService,
    private readonly toastService: ToastService,
    private readonly updatesService: UpdatesService,
    private readonly userService: UserService,
    private readonly userIdleTimeoutService: UserIdleTimeoutService,
    private readonly zendeskService: ZendeskService,
  ) {}

  ngOnInit(): void {
    removeNonAngularErrorHandlers();
    this.routeListenerService.init();
    this.analyticsService.trackRouterEvents();
    this.tenantService.populate();
    this.authService.init();
    this.startAuthWatcher()
      .pipe(
        finalize(() => {
          this.userService.populate();
          defer(() => {
            this.updatesService.init();
          });
        }),
      )
      .subscribe();
    const user$ = this.user.pipe(
      filter((user: Account) => !isEmpty(user)),
      take(1),
      shareReplay(1),
    );
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        window.scroll(0, 0);
        this.isEmptyState = this.routeListenerService.findDataValue(
          this.activatedRoute.snapshot,
          'isEmptyState',
        ) as boolean;
        this.routeTitle = this.getRouteTitle(
          this.router.routerState.root.firstChild,
        );
      });
    user$
      .pipe(
        tap((user) => {
          this.initSessionIdleTimeout(user);
          this.postHogService.identifyAccount(user);
          this.zendeskService.updateUserProperties(user);
          this.showAIAnnouncementModal(user);
          this.updateUserTimezone(user);
        }),
        delay(2000),
        tap(() => this.zendeskService.shutdown()),
      )
      .subscribe();
    this.toastService.errorToast$
      .pipe(
        concatMap((errorMessage) =>
          this.loggerService.logInfo({
            message: 'Toast - Shown (Error)',
            properties: { errorMessage },
          }),
        ),
        takeUntil(this.componentDestroy),
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.componentDestroy.next();
    this.componentDestroy.complete();
  }

  initSessionIdleTimeout(user: Account) {
    return this.tenant
      .pipe(
        filter((tenant: Tenant) => !isEmpty(tenant)),
        tap((tenant: Tenant) => {
          let idleTimeout: number | undefined;
          if (
            tenant.enabled_features.admin_lockout &&
            tenant.admin_lockout_time_minutes &&
            isAnyAdmin(user)
          ) {
            idleTimeout = tenant.admin_lockout_time_minutes;
          }
          if (
            tenant.enabled_features.session_idle_timeout &&
            tenant.session_idle_timeout_minutes &&
            (!idleTimeout || idleTimeout > tenant.session_idle_timeout_minutes)
          ) {
            idleTimeout = tenant.session_idle_timeout_minutes;
          }
          if (idleTimeout) {
            this.userIdleTimeoutService.setConfig({
              timeout: idleTimeout * 60_000,
            });
            return this.userIdleTimeoutService.start();
          }
          this.userIdleTimeoutService.stop();
        }),
        takeUntil(this.componentDestroy),
      )
      .subscribe();
  }

  showAIAnnouncementModal(user: Account) {
    if (
      this.postHogService.isFeatureEnabled(POSTHOG_FEATURE_FLAGS.AI_BETA_V1) &&
      !userHasSeenFeature(user, FEATURES_SEEN.AI_ANNOUNCEMENT_v1)
    ) {
      this.aiAnnouncementModalService
        .openDialog({ hasAccess: true })
        .afterClosed()
        .pipe(
          concatMap(({ shouldRedirect }) =>
            this.accountBackend
              .postFeatureSeen(FEATURES_SEEN.AI_ANNOUNCEMENT_v1)
              .pipe(
                tap(() =>
                  this.store.dispatch(
                    SetUser({
                      features_seen: [
                        ...(user.features_seen ?? []),
                        FEATURES_SEEN.AI_ANNOUNCEMENT_v1,
                      ],
                    }),
                  ),
                ),
                filter(() => shouldRedirect),
                concatMap(() =>
                  currentGroupSource(this.store, this.destroyService).pipe(
                    filter((group) => !!group),
                    take(1),
                    tap((group) => {
                      this.router.navigate([
                        '/hub',
                        group.slug,
                        'manage-programs',
                      ]);
                    }),
                  ),
                ),
                takeUntil(this.componentDestroy),
              ),
          ),
        )
        .subscribe();
    }
  }

  updateUserTimezone({ timezone: userTimezone }: Account) {
    const machineTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (userTimezone !== machineTimezone) {
      this.accountBackend
        .patchAccount({
          timezone: machineTimezone,
        })
        .pipe(
          tap(({ data: updatedUser }) => {
            this.store.dispatch(SetUser(updatedUser));
          }),
          takeUntil(this.componentDestroy),
        )
        .subscribe();
    }
  }

  getRouteTitle(currentRoute: ActivatedRoute): string {
    let title: string;

    while (currentRoute) {
      if (currentRoute.snapshot.routeConfig?.title) {
        title = currentRoute.snapshot.routeConfig.title as string;
      }

      currentRoute = currentRoute.firstChild;
    }

    return title;
  }

  startAuthWatcher() {
    return this.authService
      .isLoggedIn$()
      .pipe(takeWhile((isLoggedIn) => !isLoggedIn));
  }
}
